import { DateTime } from "luxon";
import {
  AdminAllLocationsCustomerResDto,
  AdminGetRoutesResDto,
} from "service/api/estabrookSchemas";
import { getActualStartTime } from "../../utils/route-planner";
import { DriverMarker, MapMarker, OrderStatus } from "./map.types";

export const transformRoutesToMapMarkers = (
  data?: AdminGetRoutesResDto[],
): (MapMarker | undefined)[] => {
  if (!data) return [];

  return data.flatMap((route) =>
    route.locations?.map((location) => ({
      id: location.locationId,
      latitude: location.latitude,
      longitude: location.longitude,
      address: location.address,
      locationSequence: location.locationSequence,
      customer: location.customer as AdminAllLocationsCustomerResDto,
      routeId: route.id,
      routeName: route.routeName ?? "",
      driverName: route.driver?.name,
      color: route.color ?? "",
      status: location.order?.status as string,
      region: route.region,
      order: location.order as OrderStatus,
      duration: location.serviceTime,
      driverStatus: location.driverStatus,
      driverStatusLastUpdatedAt: location.driverStatusLastUpdatedAt,
      driverStatusChangeReason: location.driverStatusChangeReason,
      driverStatusChangeReasonComment: location.driverStatusChangeReasonComment,
      customerType: location.type,
      locationNoteCount: location.locationNoteCount,
      travelDetails: location.travelDetails,
      routeStartTime: route.startTime,
      snowAmount: location.snowAmount,
      lastPlowedAt: location.lastPlowedAt,
    })),
  );
};

export const transformRoutesToDriverMarkers = (
  data?: AdminGetRoutesResDto[],
): DriverMarker[] => {
  if (!data) return [];

  // A driver can have several routes on a day; the marker shows when the first one starts.
  // "HH:mm" strings sort the same as the times they represent.
  const earliestStartTimeByDriver = new Map<string, string>();
  const actualStartTimeByDriver = new Map<string, string>();
  data.forEach(({ driverId, startTime, locations, state }) => {
    if (!driverId) return;

    const current = earliestStartTimeByDriver.get(driverId);
    if (startTime && (!current || startTime < current)) {
      earliestStartTimeByDriver.set(driverId, startTime);
    }

    const routeActual = getActualStartTime(locations, state);
    const currentActual = actualStartTimeByDriver.get(driverId);
    if (
      routeActual &&
      (!currentActual ||
        DateTime.fromISO(routeActual) < DateTime.fromISO(currentActual))
    ) {
      actualStartTimeByDriver.set(driverId, routeActual);
    }
  });

  const markers = data
    .map((route) => ({
      driverId: route.driverId,
      color: route.region?.color?.toLocaleLowerCase() || (route.color ?? ""),
      startingPoint: route.startingPoint,
      startTime: route.driverId
        ? earliestStartTimeByDriver.get(route.driverId)
        : undefined,
      actualStartTime: route.driverId
        ? actualStartTimeByDriver.get(route.driverId)
        : undefined,
      fullName: route.driver?.name,
    }))
    .filter((marker) => marker.driverId !== null);

  const uniqueMarkers = Array.from(
    new Map(markers.map((marker) => [marker.driverId, marker])).values(),
  );

  return uniqueMarkers;
};
